import React from 'react';
import { motion } from 'motion/react';
import { Shield, ArrowRight, CheckCircle2, Lock, Activity, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import MultiStepLeadForm from '@/src/components/MultiStepLeadForm';

export default function AdLanding() {
  return (
    <div className="relative pt-32 pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left Side: Copy */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
            >
              <Zap className="w-3 h-3" /> Priority Access
            </motion.div>

            <div className="space-y-6">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-7xl font-display font-bold tracking-tighter leading-[0.9]"
              >
                Is Your Business <br />
                <span className="text-accent glow-text">Actually Secure?</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground font-light max-w-xl leading-relaxed"
              >
                90% of small to mid-size businesses have critical security gaps they don't know about. Don't wait for a breach to find yours.
              </motion.p>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-accent group-hover:bg-accent/10 transition-colors">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Full Vulnerability Scan</h3>
                  <p className="text-sm text-muted-foreground">Identify every entry point for hackers.</p>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-accent group-hover:bg-accent/10 transition-colors">
                  <Lock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Compliance Gap Analysis</h3>
                  <p className="text-sm text-muted-foreground">Ensure you meet SOC2, HIPAA, or PCI standards.</p>
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-accent group-hover:bg-accent/10 transition-colors">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Risk Intelligence Report</h3>
                  <p className="text-sm text-muted-foreground">Get a clear roadmap to total security.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="p-8 rounded-3xl bg-accent/5 border border-accent/20 flex items-center gap-6"
            >
              <div className="flex -space-x-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-12 h-12 rounded-full border-2 border-background bg-muted overflow-hidden">
                    <img src={`https://picsum.photos/seed/user${i}/100/100`} alt="User" referrerPolicy="no-referrer" />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm font-bold">Trusted by 500+ CEOs</p>
                <p className="text-xs text-muted-foreground">Join the elite network of secure businesses.</p>
              </div>
            </motion.div>
          </div>

          {/* Right Side: Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-accent/20 blur-[60px] rounded-full opacity-30" />
            <div className="relative glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl">
              <div className="text-center mb-10 space-y-2">
                <h2 className="text-3xl font-display font-bold">Claim Your <span className="text-accent">Free Audit</span></h2>
                <p className="text-sm text-muted-foreground font-light italic">Valued at $1,500 — Limited to 5 companies per month.</p>
              </div>
              <MultiStepLeadForm />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="mt-32 py-12 border-y border-white/5 bg-white/[0.02]">
        <div className="container mx-auto px-4 flex flex-wrap justify-center gap-12 md:gap-24 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Shield className="w-6 h-6 text-accent" /> SECURECORP</div>
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Lock className="w-6 h-6 text-accent" /> DATAGUARD</div>
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Activity className="w-6 h-6 text-accent" /> NETSHIELD</div>
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Users className="w-6 h-6 text-accent" /> TRUSTLINK</div>
        </div>
      </div>
    </div>
  );
}
